import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import RadioButtonCheckedRoundedIcon from '@mui/icons-material/RadioButtonCheckedRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import {
    closestAccepted, gradesForText, matchingAccepted, shows, SpeakingTarget, TypeSpeakExercise,
} from '../../../student/mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT, yellowButtonSx } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import RecordAnswerPanel from '../../../student/components/exercises/RecordAnswerPanel';
import SpeakingHero from '../../../student/components/exercises/SpeakingHero';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';

interface TypeSpeakExerciseCardProps {
    exercise: TypeSpeakExercise;
    onReady: ExerciseReady;
}

// Simulated server check latency (exact match, then the LLM).
const CHECK_MS = 900;

// "Prompt, Type, Speak" (V1) and its V2.
//  V1: the answer must match an accepted answer; the closest one is spoken.
//  V2: an LLM approves or rejects any answer; an approved answer is spoken as typed.
// A rejected answer shows the teacher's reason and must be fixed. After two
// rejections the student may skip writing and is given a model to say — skipping
// the writing never skips the speaking.
export default function TypeSpeakExerciseCard({ exercise, onReady }: TypeSpeakExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [answer, setAnswer] = React.useState('');
    const [checking, setChecking] = React.useState(false);
    const [rejections, setRejections] = React.useState(0);
    const [rejectedAnswer, setRejectedAnswer] = React.useState<string | null>(null);
    const [target, setTarget] = React.useState<SpeakingTarget | null>(null);
    const [skippedWriting, setSkippedWriting] = React.useState(false);
    const timeoutRef = React.useRef<number>();

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const check = () => {
        setChecking(true);
        timeoutRef.current = window.setTimeout(() => {
            setChecking(false);
            const typed = answer.trim();
            if (exercise.version === 1) {
                // Mock: exact match against the accepted list stands in for exact + LLM.
                const hit = matchingAccepted(typed, exercise.accepted);
                if (hit) {
                    setTarget(hit);
                    return;
                }
            } else if ((exercise.mustInclude ?? []).every(token => typed.includes(token)) && typed.length >= 3) {
                setTarget({ hanzi: typed, pinyin: '', grades: gradesForText(typed) });
                return;
            }
            setRejections(count => count + 1);
            setRejectedAnswer(typed);
        }, CHECK_MS);
    };

    const skipWriting = () => {
        setSkippedWriting(true);
        setTarget(exercise.version === 1 ? closestAccepted(answer, exercise.accepted) : exercise.accepted[0]);
    };

    const writingDone = target != null;
    const stillRejected = rejectedAnswer != null && rejectedAnswer === answer.trim();

    const steps = [
        { label: '1 Write your answer', active: !writingDone, complete: writingDone },
        { label: '2 Say it aloud', active: writingDone, complete: false },
    ];

    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <SpeakingHero
                label={exercise.version === 2 ? 'WRITE YOUR OWN, THEN SAY IT' : 'TYPE, THEN SAY'}
                hanzi={exercise.question.hanzi}
                pinyin={shows(exercise, 'pinyin') ? exercise.question.pinyin : undefined}
            />

            <Box sx={{ p: desktop ? 3 : 2.5 }}>
                <Stack direction="row" spacing={1} role="list" aria-label="Activity steps" sx={{ mb: 2.5 }}>
                    {steps.map(step => {
                        const StepIcon = step.complete ? CheckCircleRoundedIcon : step.active ? RadioButtonCheckedRoundedIcon : CircleOutlinedIcon;
                        return (
                            <Box
                                key={step.label}
                                role="listitem"
                                aria-current={step.active ? 'step' : undefined}
                                aria-label={`${step.label}${step.complete ? ', complete' : ''}`}
                                sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, borderRadius: '12px', bgcolor: step.active ? `${BLUE}12` : SURFACE_SOFT, color: step.active ? BLUE : SLATE }}
                            >
                                <StepIcon aria-hidden="true" sx={{ fontSize: 17, flexShrink: 0 }} />
                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5 }}>{step.label}</Typography>
                            </Box>
                        );
                    })}
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${BLUE}1A`,
                    }}>
                        <VolumeUpRoundedIcon sx={{ color: BLUE }} />
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, flex: 1, minWidth: 0 }}>
                        {shows(exercise, 'english') ? exercise.question.english : 'Play the question'}
                    </Typography>
                    <AudioPlayButton ariaLabel="Play the question" size={40} />
                </Stack>

                <Box sx={{ display: desktop && writingDone ? 'grid' : 'block', gridTemplateColumns: 'minmax(0, .9fr) minmax(0, 1.1fr)', gap: 3, alignItems: 'start' }}>
                    <Box>
                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 2.5 }}>
                            {exercise.teacher?.intro ?? exercise.instruction}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            value={answer}
                            disabled={writingDone || checking}
                            onChange={event => setAnswer(event.target.value)}
                            placeholder="在这里写你的回答……"
                            inputProps={{ 'aria-label': 'Your answer' }}
                            sx={{
                                mt: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '16px',
                                    backgroundColor: SURFACE_SOFT,
                                    fontFamily: POPPINS,
                                    fontSize: 18,
                                    color: NAVY,
                                    '& fieldset': { borderColor: RING_TRACK },
                                },
                            }}
                        />
                        {!writingDone && (
                            <Button
                                fullWidth
                                variant="contained"
                                disabled={checking || answer.trim().length === 0 || stillRejected}
                                onClick={check}
                                startIcon={checking ? <CircularProgress size={18} /> : undefined}
                                sx={{ ...yellowButtonSx, py: 1.25, mt: 1.5 }}
                            >
                                {checking ? 'Checking…' : stillRejected ? 'Fix your answer to check again' : 'Check my answer'}
                            </Button>
                        )}
                        {!writingDone && rejectedAnswer != null && (
                            <Box sx={{ mt: 1.5 }}>
                                <TeacherNote tone="warning" text={exercise.teacher?.incorrect ?? exercise.reasonForIncorrect} />
                            </Box>
                        )}
                        {!writingDone && rejections >= 2 && (
                            <Button onClick={skipWriting} sx={{ minHeight: 44, mt: 1, fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, textTransform: 'none' }}>
                                Skip writing — show me an answer to say
                            </Button>
                        )}
                        {writingDone && (
                            <Box sx={{ mt: 1.5 }}>
                                <TeacherNote
                                    tone={skippedWriting ? 'info' : 'success'}
                                    text={skippedWriting
                                        ? 'Here is an answer you could give. Say it out loud.'
                                        : exercise.teacher?.correct ?? '对了! That works — now say it out loud.'}
                                />
                            </Box>
                        )}
                    </Box>

                    {target && (
                        <Box sx={{ mt: desktop ? 2.5 : 2.5 }}>
                            {exercise.teacher?.askToSpeak ? (
                                <Box sx={{ mb: 1.5 }}><TeacherNote text={exercise.teacher.askToSpeak} /></Box>
                            ) : (
                                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, mb: 1 }}>
                                    {exercise.version === 2 && !skippedWriting ? 'SAY YOUR SENTENCE' : 'SAY THIS'}
                                </Typography>
                            )}
                            <Box sx={{ p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 19, color: NAVY, lineHeight: 1.4 }}>
                                        {target.hanzi}
                                    </Typography>
                                    {shows(exercise, 'pinyin') && target.pinyin && (
                                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                            {target.pinyin}
                                        </Typography>
                                    )}
                                </Box>
                                <AudioPlayButton ariaLabel="Play the model answer" size={40} />
                            </Box>
                            <RecordAnswerPanel
                                key={target.hanzi}
                                grades={target.grades}
                                takes={target.takes}
                                correctText={exercise.teacher?.spokenCorrect}
                                incorrectText={exercise.teacher?.spokenIncorrect}
                                onComplete={() => onReady('done')}
                                onSkippable={() => onReady('skip')}
                            />
                        </Box>
                    )}
                </Box>
                <ExerciseSupport exercise={exercise} />
            </Box>
        </Box>
    );
}
